import { AlertTriangle, BarChart2, BellRing, CheckCircle, Clock, Flame, Lightbulb, Mail, MessageSquare, Rocket, Send, Sparkles, Star, Users, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import {
    getNotificationHub,
    snoozeNotification,
    markAsContacted,
    NotificationItem,
    NotificationSummary,
} from "../../interfaces/notification";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

type Tone = "danger" | "warning" | "info" | "success" | "neutral";

const TYPE_CONFIG: Record<string, { icon: React.ElementType; tone: Tone; label: string }> = {
    overdue: { icon: AlertTriangle, tone: "danger", label: "En retard" },
    today: { icon: Clock, tone: "warning", label: "Aujourd'hui" },
    response: { icon: MessageSquare, tone: "info", label: "Réponse reçue" },
    hot: { icon: Flame, tone: "danger", label: "Prospect chaud" },
    tomorrow: { icon: Clock, tone: "warning", label: "Demain" },
    "this-week": { icon: Clock, tone: "success", label: "Cette semaine" },
    "not-contacted": { icon: Users, tone: "neutral", label: "Non contacté" },
    stale: { icon: AlertTriangle, tone: "neutral", label: "Inactif" },
    "no-action": { icon: Sparkles, tone: "neutral", label: "Sans action" },
};

const TONE_ICON_CLASSES: Record<Tone, string> = {
    danger: "text-danger",
    warning: "text-warning",
    info: "text-info",
    success: "text-success",
    neutral: "text-ink-faint",
};

function getTypeConfig(type: string) {
    return TYPE_CONFIG[type] ?? { icon: BellRing, tone: "neutral" as Tone, label: type };
}

const NotificationHub = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [summary, setSummary] = useState<NotificationSummary | null>(null);
    const [filter, setFilter] = useState<string>("all");
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        loadNotifications();
    }, []);

    const loadNotifications = async () => {
        setLoading(true);
        const data = await getNotificationHub();
        if (data) {
            setNotifications(data.notifications);
            setSummary(data.summary);
        }
        setLoading(false);
    };

    const handleSnooze = async (prospectId: string, days: number) => {
        setActionLoading(prospectId);
        await snoozeNotification(prospectId, days);
        await loadNotifications();
        setActionLoading(null);
    };

    const handleMarkContacted = async (prospectId: string, type: string) => {
        setActionLoading(prospectId);
        await markAsContacted(prospectId, type);
        await loadNotifications();
        setActionLoading(null);
    };

    const handleViewProspect = (prospectId: string) => {
        navigate(`/dashboard?prospect=${prospectId}`);
    };

    const formatTimeDetail = (notification: NotificationItem) => {
        if (notification.daysOverdue !== undefined) {
            return `${notification.daysOverdue} jour${notification.daysOverdue !== 1 ? "s" : ""} de retard`;
        }
        if (notification.daysUntil !== undefined && notification.daysUntil > 0) {
            return `dans ${notification.daysUntil} jour${notification.daysUntil !== 1 ? "s" : ""}`;
        }
        if (notification.daysSinceContact !== undefined) {
            return `${notification.daysSinceContact} jours depuis le dernier contact`;
        }
        if (notification.daysSinceActivity !== undefined) {
            return `${notification.daysSinceActivity} jours d'inactivité`;
        }
        return "";
    };

    const filteredNotifications =
        filter === "all"
            ? notifications
            : notifications.filter((n) => n.type === filter);

    const urgentCount = notifications.filter(n => n.type === "overdue" || n.type === "today").length;

    const FILTERS: { key: string; label: string; count: number }[] = [
        { key: "all", label: "Tous", count: notifications.length },
        { key: "overdue", label: "En retard", count: summary?.overdue || 0 },
        { key: "today", label: "Aujourd'hui", count: summary?.dueToday || 0 },
        { key: "response", label: "Réponses", count: summary?.recentResponses || 0 },
        { key: "hot", label: "Chauds", count: summary?.hotProspects || 0 },
        { key: "not-contacted", label: "Non contactés", count: summary?.notContactedRecently || 0 },
    ];

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="bg-ink p-3 rounded-lg">
                                    <Sparkles className="h-6 w-6 text-ink-inverse" />
                                </div>
                                <div>
                                    <h1 className="font-display text-3xl font-normal text-ink">
                                        Copilote Commercial IA
                                    </h1>
                                    <p className="mt-1 text-sm text-ink-muted">
                                        Vos prochaines actions prioritaires, optimisées par l'IA
                                    </p>
                                </div>
                            </div>

                            {/* Urgent insight */}
                            {!loading && urgentCount > 0 && (
                                <div className="mt-4 bg-warning-subtle border-l-4 border-warning rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                        <Zap className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-warning">
                                                Action immédiate requise
                                            </p>
                                            <p className="text-xs text-warning/80 mt-1">
                                                {urgentCount} prospect{urgentCount > 1 ? "s nécessitent" : " nécessite"} votre attention aujourd'hui pour maximiser vos chances de conversion
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Button onClick={loadNotifications} disabled={loading}>
                            <Sparkles className="h-4 w-4" />
                            {loading ? "Actualisation..." : "Actualiser"}
                        </Button>
                    </div>
                </div>

                {/* Metrics */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                        <div className="bg-surface border border-border rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <AlertTriangle className="h-5 w-5 text-danger" />
                                <span className="text-2xl font-semibold tracking-tight text-ink">{summary.overdue}</span>
                            </div>
                            <p className="text-xs font-semibold text-ink-faint uppercase tracking-wide">En retard</p>
                        </div>

                        <div className="bg-surface border border-border rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <Clock className="h-5 w-5 text-warning" />
                                <span className="text-2xl font-semibold tracking-tight text-ink">{summary.dueToday}</span>
                            </div>
                            <p className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Aujourd'hui</p>
                        </div>

                        <div className="bg-surface border border-border rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <MessageSquare className="h-5 w-5 text-info" />
                                <span className="text-2xl font-semibold tracking-tight text-ink">{summary.recentResponses}</span>
                            </div>
                            <p className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Réponses</p>
                        </div>

                        <div className="bg-surface border border-border rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <Flame className="h-5 w-5 text-danger" />
                                <span className="text-2xl font-semibold tracking-tight text-ink">{summary.hotProspects}</span>
                            </div>
                            <p className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Prospects chauds</p>
                        </div>

                        <div className="bg-surface border border-border rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <Users className="h-5 w-5 text-ink-muted" />
                                <span className="text-2xl font-semibold tracking-tight text-ink">{summary.notContactedRecently}</span>
                            </div>
                            <p className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Non contactés</p>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-surface border border-border rounded-2xl p-5 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <BarChart2 className="h-4 w-4 text-ink-muted" />
                        <h3 className="text-xs font-semibold text-ink-faint uppercase tracking-wide">Filtrer par priorité</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                                    filter === f.key
                                        ? "bg-ink text-ink-inverse"
                                        : "bg-surface border border-border text-ink-muted hover:border-border-strong"
                                }`}
                            >
                                {f.label} ({f.count})
                            </button>
                        ))}
                    </div>
                </div>

                {/* Notifications list */}
                <div className="space-y-4">
                    {loading ? (
                        <div className="bg-surface border border-border rounded-2xl p-12 text-center">
                            <div className="mx-auto w-12 h-12 mb-6 motion-safe:animate-spin rounded-full border-4 border-border border-t-ink" />
                            <p className="text-ink text-sm font-medium">Analyse de vos prospects en cours...</p>
                            <p className="text-ink-muted text-xs mt-2">L'IA optimise vos prochaines actions</p>
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="bg-success-subtle border border-success/20 rounded-2xl p-12 text-center">
                            <CheckCircle className="h-14 w-14 text-success mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-success mb-3">
                                Excellent travail !
                            </h3>
                            <p className="text-sm text-success/80 max-w-md mx-auto">
                                {filter !== "all"
                                    ? `Aucune tâche en attente pour "${getTypeConfig(filter).label}"`
                                    : "Toutes vos actions sont à jour. Votre pipeline est optimisé !"}
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-surface text-success rounded-full text-xs font-medium">
                                <Star className="h-4 w-4" />
                                Productivité maximale atteinte
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Priority banner */}
                            <div className="bg-ink text-ink-inverse rounded-2xl p-6 mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="bg-white/10 p-3 rounded-lg">
                                        <Rocket className="h-7 w-7" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold mb-1">
                                            {filteredNotifications.length} action{filteredNotifications.length > 1 ? "s" : ""} recommandée{filteredNotifications.length > 1 ? "s" : ""} par l'IA
                                        </h3>
                                        <p className="text-sm text-ink-inverse-muted">
                                            Traitez-les dans l'ordre pour optimiser votre taux de conversion
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-semibold">{filteredNotifications.length}</div>
                                        <div className="text-xs text-ink-inverse-muted">tâches</div>
                                    </div>
                                </div>
                            </div>

                            {filteredNotifications.map((notification, idx) => {
                                const cfg = getTypeConfig(notification.type);
                                const Icon = cfg.icon;
                                return (
                                    <div
                                        key={`${notification.prospect._id}-${idx}`}
                                        className="bg-surface border border-border rounded-2xl p-6 hover:border-border-strong transition-colors duration-150"
                                    >
                                        <div className="flex items-start gap-5">
                                            {/* Priority Indicator & Icon */}
                                            <div className="flex-shrink-0">
                                                <div className="bg-surface-sunken p-4 rounded-2xl border border-border">
                                                    <Icon className={`h-6 w-6 ${TONE_ICON_CLASSES[cfg.tone]}`} />
                                                </div>
                                                <div className="text-center mt-2">
                                                    <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink text-ink-inverse text-xs font-semibold">
                                                        #{idx + 1}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                {/* Header */}
                                                <div className="flex items-start justify-between gap-4 mb-3">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <h3 className="text-lg font-semibold text-ink">
                                                                {`${notification.prospect.firstName || ""} ${
                                                                    notification.prospect.lastName || ""
                                                                }`.trim() || "Inconnu"}
                                                            </h3>
                                                            <Badge tone={cfg.tone}>{cfg.label}</Badge>
                                                        </div>
                                                        {notification.prospect.company && (
                                                            <p className="text-sm text-ink-muted">
                                                                {notification.prospect.company}
                                                                {notification.prospect.title &&
                                                                    ` · ${notification.prospect.title}`}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* AI Reasoning */}
                                                <div className="mb-4 p-4 bg-warning-subtle rounded-lg border-l-4 border-warning">
                                                    <div className="flex items-start gap-2">
                                                        <Lightbulb className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                                                        <div>
                                                            <p className="text-xs font-semibold text-warning uppercase tracking-wide mb-1">
                                                                Recommandation IA
                                                            </p>
                                                            <p className="text-sm text-warning/90">
                                                                {notification.reason}
                                                            </p>
                                                            {formatTimeDetail(notification) && (
                                                                <p className="text-xs text-warning/70 mt-1.5">
                                                                    {formatTimeDetail(notification)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Next Action Box */}
                                                {notification.prospect.nextAction && (
                                                    <div className="mb-4 p-4 bg-info-subtle rounded-lg border border-info/20">
                                                        <div className="flex items-start gap-2">
                                                            <Zap className="h-5 w-5 text-info flex-shrink-0 mt-0.5" />
                                                            <div className="flex-1">
                                                                <p className="text-xs text-info font-semibold uppercase tracking-wide mb-1.5">
                                                                    Prochaine action planifiée
                                                                </p>
                                                                <p className="text-sm text-info font-medium">
                                                                    {notification.prospect.nextAction}
                                                                </p>
                                                                {notification.prospect.nextActionAt && (
                                                                    <p className="text-xs text-info/70 mt-2">
                                                                        {new Date(
                                                                            notification.prospect.nextActionAt
                                                                        ).toLocaleDateString("fr-FR", {
                                                                            weekday: "long",
                                                                            month: "long",
                                                                            day: "numeric",
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                        })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action Buttons */}
                                                <div className="flex flex-wrap gap-2">
                                                    <Button size="sm" onClick={() => handleViewProspect(notification.prospect._id)}>
                                                        Voir le détail
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => handleMarkContacted(notification.prospect._id, "send-email")}
                                                        disabled={actionLoading === notification.prospect._id}
                                                    >
                                                        <Mail className="h-4 w-4" />
                                                        Email envoyé
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => handleMarkContacted(notification.prospect._id, "send-linkedin")}
                                                        disabled={actionLoading === notification.prospect._id}
                                                    >
                                                        <Send className="h-4 w-4" />
                                                        LinkedIn
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleSnooze(notification.prospect._id, 1)}
                                                        disabled={actionLoading === notification.prospect._id}
                                                    >
                                                        <Clock className="h-4 w-4" />
                                                        +1j
                                                    </Button>

                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleSnooze(notification.prospect._id, 7)}
                                                        disabled={actionLoading === notification.prospect._id}
                                                    >
                                                        <Clock className="h-4 w-4" />
                                                        +7j
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Sales tips */}
                {!loading && filteredNotifications.length > 0 && (
                    <div className="mt-8 bg-surface border border-border rounded-2xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 bg-accent-subtle p-3 rounded-lg">
                                <Sparkles className="h-5 w-5 text-accent" />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-xs font-semibold text-ink-faint mb-3 uppercase tracking-wide">
                                    Conseils IA pour maximiser vos conversions
                                </h4>
                                <div className="space-y-3">
                                    {[
                                        { n: 1, title: "Priorité absolue :", text: "Les prospects en retard et ceux à contacter aujourd'hui ont le ROI le plus élevé" },
                                        { n: 2, title: "Réactivité :", text: "Répondez aux messages reçus sous 24h pour maximiser vos chances de conversion (jusqu'à +65%)" },
                                        { n: 3, title: "Prospects chauds :", text: "Maintenez un engagement constant - ne les laissez pas refroidir (relance tous les 3-5 jours)" },
                                        { n: 4, title: "Réactivation :", text: "Les prospects non contactés depuis 30+ jours nécessitent une nouvelle proposition de valeur personnalisée" },
                                    ].map((tip) => (
                                        <div key={tip.n} className="flex items-start gap-3 bg-surface-sunken rounded-lg p-3">
                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-ink text-ink-inverse flex items-center justify-center text-xs font-semibold">
                                                {tip.n}
                                            </div>
                                            <p className="text-sm text-ink-muted">
                                                <span className="font-semibold text-ink">{tip.title}</span> {tip.text}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Daily goal */}
                                <div className="mt-4 flex items-center gap-3 p-3 bg-success-subtle rounded-lg border border-success/20">
                                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-success flex items-center justify-center">
                                        <BarChart2 className="h-4 w-4 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-success uppercase tracking-wide">Objectif journalier</p>
                                        <p className="text-sm text-success/90 mt-0.5">
                                            Traitez au moins {Math.max(3, Math.min(10, filteredNotifications.length))} prospects par jour pour maintenir votre pipeline actif
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationHub;
